import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, MapPin, Check, CalendarBlank, WhatsappLogo, FilePdf, CreditCard } from "@phosphor-icons/react";
import { api, inr } from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";
import BookNowDialog, { ADVANCE_PERCENT } from "@/components/BookNowDialog";
import { CONTACT } from "@/data/site";

export default function PackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);

  useEffect(() => {
    api.get(`/packages/${id}`).then((r) => setPkg(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return (
    <div className="pt-40 pb-32 text-center">
      <p className="text-[#8B89A4]">This package could not be found.</p>
      <Link to="/packages" className="mt-4 inline-block text-brand-teal font-medium">Back to packages</Link>
    </div>
  );
  if (!pkg) return <div className="pt-40 pb-40 text-center text-[#8B89A4]">Loading...</div>;

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[70vh] min-h-[480px] flex items-end overflow-hidden" data-testid="pkg-hero">
        <motion.img
          src={pkg.image} alt={pkg.title}
          initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/85 via-brand-navy/30 to-brand-navy/20" />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-5 md:px-10 pb-14">
          <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-2 text-white/80 text-sm hover:text-white transition-colors" data-testid="pkg-back">
            <ArrowLeft size={18} /> Back
          </button>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-brand-navy">
            <MapPin size={14} weight="fill" className="text-brand-orange" /> {pkg.country_state} · {pkg.region}
          </span>
          <h1 className="mt-5 text-4xl sm:text-6xl font-heading font-medium text-white max-w-3xl leading-tight">{pkg.title}</h1>
          <p className="mt-3 text-white/80 font-body">{pkg.destination}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid lg:grid-cols-3 gap-14">
        <div className="lg:col-span-2 space-y-12">
          <div className="flex flex-wrap gap-8 border-b border-brand-sand pb-8">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#8B89A4] flex items-center gap-1.5"><Clock size={14} /> Duration</p>
              <p className="mt-1 font-heading text-xl text-brand-navy">{pkg.duration}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#8B89A4]">Starting from</p>
              <p className="mt-1 font-heading text-xl text-brand-orange">{inr(pkg.price)} <span className="text-sm text-[#8B89A4]">/ {pkg.price_note || "person"}</span></p>
            </div>
            {pkg.hotel_category && (
              <div>
                <p className="text-xs uppercase tracking-wider text-[#8B89A4]">Hotels</p>
                <p className="mt-1 font-heading text-xl text-brand-navy">{pkg.hotel_category}</p>
              </div>
            )}
          </div>

          <div>
            <h2 className="text-3xl font-heading font-medium text-brand-navy mb-4">Overview</h2>
            <p className="text-[#5b5980] font-body leading-relaxed text-lg">{pkg.description || pkg.summary}</p>
          </div>

          {pkg.highlights?.length > 0 && (
            <div>
              <h2 className="text-3xl font-heading font-medium text-brand-navy mb-5">Highlights</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {pkg.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-xl bg-brand-sand p-4">
                    <Check size={20} weight="bold" className="text-brand-teal shrink-0 mt-0.5" />
                    <span className="text-brand-navy font-body text-sm">{h}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pkg.itinerary?.length > 0 && (
            <div>
              <h2 className="text-3xl font-heading font-medium text-brand-navy mb-6">Day-by-day itinerary</h2>
              <div className="space-y-5">
                {pkg.itinerary.map((d, i) => (
                  <div key={i} className="flex gap-5">
                    <div className="flex flex-col items-center">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-teal text-white font-accent font-bold text-sm">D{d.day}</span>
                      {i < pkg.itinerary.length - 1 && <span className="w-px flex-1 bg-brand-sand my-2" />}
                    </div>
                    <div className="pb-4">
                      <h3 className="font-heading text-xl text-brand-navy">{d.title}</h3>
                      <p className="mt-1 text-[#6b6a8a] font-body text-sm leading-relaxed">{d.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pkg.itinerary_pdf && (
            <div className="rounded-2xl border border-brand-teal/30 bg-brand-teal/5 p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="pkg-pdf-block">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal text-white"><FilePdf size={26} weight="fill" /></span>
                <div>
                  <h3 className="font-heading text-xl text-brand-navy">Full day-by-day itinerary</h3>
                  <p className="text-sm text-[#6b6a8a]">Detailed plan with hotels, sightseeing & inclusions.</p>
                </div>
              </div>
              <a
                href={pkg.itinerary_pdf}
                target="_blank"
                rel="noreferrer"
                data-testid="pkg-view-itinerary"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-navydark whitespace-nowrap"
              >
                <FilePdf size={18} weight="fill" /> View Itinerary PDF
              </a>
            </div>
          )}
        </div>

        {/* Sticky enquiry */}
        <div className="lg:sticky lg:top-28 h-fit">
          <div className="rounded-3xl bg-white border border-brand-sand p-7 shadow-[0_18px_50px_-20px_rgba(46,42,128,0.25)]" data-testid="pkg-enquiry">
            <div className="flex items-center gap-2 text-brand-navy mb-1">
              <CalendarBlank size={22} className="text-brand-orange" weight="duotone" />
              <h3 className="font-heading text-2xl">Enquire about this trip</h3>
            </div>
            <p className="text-sm text-[#8B89A4] mb-5">We’ll tailor it to your dates & budget.</p>
            <button
              onClick={() => setBookOpen(true)}
              data-testid="pkg-pay-advance"
              className="mb-3 flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
            >
              <CreditCard size={20} weight="fill" /> Book Now — Pay {ADVANCE_PERCENT}% Advance
            </button>
            <a
              href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(`Hi Cluetrip! I'm interested in the "${pkg.title}" package (${pkg.duration}, from ${inr(pkg.price)} per person). Please share more details.`)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="pkg-whatsapp-enquire"
              className="mb-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
            >
              <WhatsappLogo size={20} weight="fill" /> Enquire on WhatsApp
            </a>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px flex-1 bg-brand-sand" />
              <span className="text-xs text-[#a3a1ba] uppercase tracking-wider">or send details</span>
              <span className="h-px flex-1 bg-brand-sand" />
            </div>
            <InquiryForm compact prefillDestination={pkg.country_state} packageId={pkg.id} packageTitle={pkg.title} />
          </div>
        </div>
      </section>
      <BookNowDialog pkg={pkg} open={bookOpen} onOpenChange={setBookOpen} />
    </div>
  );
}
